import React, { useEffect, useMemo, useState, useCallback } from "react";
import ProductSearchTable from "./components/ProductSearchTable";
import useProductSearch from "./hooks/useProductSearch";
import { Button } from "primereact/button";
import { useLanguage } from "../../../contexts/LanguageContext";
import { useGetBasketQuery } from "../../../api/endpoints/basket";
import ServiceBulletinOrderModal from "../../basket/components/ServiceBulletinOrderModal";
import { createInitialFilters, convertFiltersToPrimeReactFormat } from "../../../utils/filterUtils";
import { FilterMatchMode } from "primereact/api";
import ProductSearchFilter from "./components/ProductSearchFilter";
import FloatingBasket from "./components/FloatingBasket";

const ProductSearchPage = () => {
  const { th } = useLanguage();

  const [lazyParams, setLazyParams] = useState({ page: 1, rows: 10 });
  const [filters, setFilters] = useState({});
  const [apiFilters, setApiFilters] = useState({});

  const filterFields = useMemo(
    () => [
      { field: "partCode", headerName: th("product.code"), type: "text" },
      { field: "partName", headerName: th("product.name"), type: "text" }
    ],
    [th]
  );

  const filterTypeMap = useMemo(
    () => ({
      partCode: { type: "text", matchMode: FilterMatchMode.CONTAINS },
      partName: { type: "text", matchMode: FilterMatchMode.CONTAINS }
    }),
    []
  );

  const filterFieldNames = useMemo(() => filterFields.map((f) => f.field), [filterFields]);
  const initialFilters = useMemo(
    () => createInitialFilters(filterFieldNames, filterTypeMap),
    [filterFieldNames, filterTypeMap]
  );

  useEffect(() => setFilters(initialFilters), [initialFilters]);

  const handleApplyFilters = useCallback(
    (updatedFilters) => {
      const converted = convertFiltersToPrimeReactFormat(updatedFilters, filters, filterTypeMap);
      setFilters(converted);
      const next = {};
      Object.entries(updatedFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== "" && value !== null) next[key] = value;
      });
      setApiFilters(next);
      setLazyParams((p) => ({ ...p, page: 1 }));
    },
    [filters, filterTypeMap]
  );

  const handleResetFilters = useCallback(() => {
    setFilters(initialFilters);
    setApiFilters({});
    setLazyParams((p) => ({ ...p, page: 1 }));
  }, [initialFilters]);

  const { products, totalRecords, isLoading, refetch: refetchProducts } = useProductSearch({
    page: lazyParams.page,
    rows: lazyParams.rows,
    partCode: apiFilters.partCode ?? null,
    partName: apiFilters.partName ?? null
  });

  const [serviceBulletinModalVisible, setServiceBulletinModalVisible] = useState(false);
  const { refetch: refetchBasket } = useGetBasketQuery();

  const handlePage = (event) => {
    setLazyParams({ page: event.page + 1, rows: event.rows });
  };

  return (
    <div className="card">
      <div className="flex justify-content-between align-items-center mb-3">
        <h2 className="m-0">{th("product.searchTitle")}</h2>
        <div className="flex gap-2 align-items-center">
          <Button
            label={th("basket.serviceBulletinOrder")}
            icon="pi pi-file-edit"
            className="p-button-secondary"
            onClick={() => setServiceBulletinModalVisible(true)}
          />
        </div>
      </div>

      <ProductSearchFilter
        filterFields={filterFields}
        columns={[]}
        customFilterTypes={{}}
        customFilterOptions={{}}
        handleSearch={() => {}}
        handleApplyFilters={handleApplyFilters}
        handleResetFilters={handleResetFilters}
      />

      <ProductSearchTable
        data={products}
        totalRecords={totalRecords}
        isLoading={isLoading}
        onPage={handlePage}
        lazyParams={lazyParams}
        refetchBasket={() => {
          refetchBasket();
          refetchProducts();
        }}
      />

      <ServiceBulletinOrderModal
        visible={serviceBulletinModalVisible}
        onHide={() => setServiceBulletinModalVisible(false)}
      />

      <FloatingBasket />
    </div>
  );
};

export default ProductSearchPage;
